<script lang="ts">
  /**
   * MainMenu — title screen with ASCII art and menu options.
   * Disables New World / Load World when no API key is configured.
   */
  import { keyCache } from "$lib/stores/keys";
  import { overlayStore } from "$lib/stores/game.svelte";

  interface Props {
    onResume?: () => void;
    onNewWorld: () => void;
    onManageWorlds?: () => void;
    onQuit?: () => void;
  }

  let { onResume, onNewWorld, onManageWorlds, onQuit }: Props = $props();

  let hasKey = $state(false);
  let keyCheckDone = $state(false);

  $effect(() => {
    keyCache.hasAnyKey().then((result) => {
      hasKey = result;
      keyCheckDone = true;
    });
  });

  interface MenuItem {
    label: string;
    action: (() => void) | undefined;
    enabled: boolean;
  }

  const menuItems: MenuItem[] = $derived([
    { label: "Resume Game", action: onResume, enabled: !!onResume && hasKey },
    { label: "New World", action: onNewWorld, enabled: hasKey },
    {
      label: "Manage Worlds",
      action: onManageWorlds,
      enabled: !!onManageWorlds,
    },
    { label: "Settings", action: () => overlayStore.toggle("settings"), enabled: true },
    { label: "Quit", action: onQuit, enabled: !!onQuit },
  ]);

  let selectedIndex = $state(0);

  // Skip disabled items on init
  $effect(() => {
    if (!menuItems[selectedIndex].enabled) {
      const first = menuItems.findIndex((m) => m.enabled);
      if (first !== -1) selectedIndex = first;
    }
  });

  function moveUp(): void {
    let next = selectedIndex - 1;
    while (next >= 0 && !menuItems[next].enabled) next--;
    if (next >= 0) selectedIndex = next;
  }

  function moveDown(): void {
    let next = selectedIndex + 1;
    while (next < menuItems.length && !menuItems[next].enabled) next++;
    if (next < menuItems.length) selectedIndex = next;
  }

  function confirm(): void {
    const item = menuItems[selectedIndex];
    if (item.enabled && item.action) item.action();
  }

  function handleKeydown(e: KeyboardEvent): void {
    switch (e.key) {
      case "ArrowUp":
      case "w":
      case "W":
        e.preventDefault();
        moveUp();
        break;
      case "ArrowDown":
      case "s":
      case "S":
        e.preventDefault();
        moveDown();
        break;
      case "Enter":
        e.preventDefault();
        confirm();
        break;
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="main-menu">
  <div class="main-menu__decoration">* * * * * * * * * * *</div>

  <h1 class="main-menu__title">DREADFUL<br />DEPTHS</h1>

  <p class="main-menu__subtitle">An AI-Driven Roguelike Text RPG</p>
  <p class="main-menu__version">v0.1.0</p>

  <div class="main-menu__border">
    +{"=".repeat(34)}+
  </div>

  <nav class="main-menu__nav">
    {#each menuItems as item, i}
      <button
        class="main-menu__item"
        class:main-menu__item--selected={selectedIndex === i}
        class:main-menu__item--disabled={!item.enabled}
        disabled={!item.enabled}
        onclick={() => {
          selectedIndex = i;
          confirm();
        }}
        onmouseenter={() => {
          if (item.enabled) selectedIndex = i;
        }}
      >
        {#if selectedIndex === i}{">"} {item.label} {"<"}{:else}
          {item.label}
        {/if}
      </button>
    {/each}
  </nav>

  <div class="main-menu__border">
    +{"=".repeat(34)}+
  </div>

  <div class="main-menu__decoration">* * * * * * * * * * *</div>

  {#if keyCheckDone && !hasKey}
    <p class="main-menu__nokey">
      An API key is required to play. Select Settings to configure one.
    </p>
  {/if}

  <p class="main-menu__hint">Arrow keys / W/S to navigate, Enter to select -- or tap</p>
</div>

<style>
  .main-menu {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    min-height: 100dvh;
    padding: 1.5rem 1rem;
    gap: 0.4rem;
  }

  .main-menu__title {
    font-family: var(--font-mono);
    font-size: clamp(1.5rem, 5vw, 3rem);
    letter-spacing: 0.3em;
    text-align: center;
    color: var(--fg-bright);
    text-shadow:
      0 0 10px rgba(51, 255, 51, 0.5),
      0 0 20px rgba(51, 255, 51, 0.2);
    line-height: 1.4;
    margin: 0;
    font-weight: bold;
  }

  .main-menu__decoration {
    color: var(--fg-dim);
    font-size: clamp(0.6rem, 2vw, 0.85rem);
    letter-spacing: 0.2em;
    text-align: center;
    opacity: 0.6;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 90vw;
  }

  .main-menu__subtitle {
    color: var(--fg-dim);
    font-size: clamp(0.7rem, 2.5vw, 0.9rem);
    margin-bottom: 0.25rem;
    text-align: center;
  }

  .main-menu__version {
    color: var(--fg-dim);
    font-size: 0.7rem;
    margin-bottom: 0.5rem;
    opacity: 0.6;
  }

  .main-menu__border {
    color: var(--fg-dim);
    font-size: clamp(0.65rem, 2vw, 0.85rem);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 90vw;
  }

  .main-menu__nav {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.4rem 0;
  }

  .main-menu__item {
    background: none;
    border: none;
    color: var(--fg);
    font-family: var(--font-mono);
    font-size: clamp(0.85rem, 2.5vw, 1rem);
    cursor: pointer;
    padding: 0.25rem 1rem;
    text-align: center;
    white-space: nowrap;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .main-menu__item--selected {
    color: var(--fg-bright);
    font-weight: bold;
  }

  .main-menu__item--disabled {
    color: var(--fg-dim);
    cursor: default;
  }

  .main-menu__nokey {
    color: #ffaa00;
    font-size: clamp(0.7rem, 2vw, 0.8rem);
    text-align: center;
    max-width: 36ch;
    margin-top: 0.5rem;
  }

  .main-menu__hint {
    color: var(--fg-dim);
    font-size: clamp(0.6rem, 1.8vw, 0.75rem);
    margin-top: 0.5rem;
    text-align: center;
  }

  @media (max-width: 480px) {
    .main-menu {
      padding: 1rem 0.75rem;
      gap: 0.3rem;
    }

    .main-menu__item {
      padding: 0.2rem 0.75rem;
    }
  }
</style>
